import { useState, useEffect } from 'react';
import { Star, Trash2, Loader2 } from 'lucide-react';
import type { LetterRating, LetterRatingInput } from '../../../lib/api';

interface RatingPanelProps {
  existingRating: LetterRating | null;
  isLoading: boolean;
  onSubmit: (data: LetterRatingInput) => Promise<void>;
  onDelete: () => Promise<void>;
}


function StarRating({
  value,
  onChange,
  disabled,
}: {
  value: number;
  onChange: (v: number) => void;
  disabled?: boolean;
}) {
  const [hover, setHover] = useState(0);

  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={disabled}
          onMouseEnter={() => setHover(star)}
          onMouseLeave={() => setHover(0)}
          onClick={() => onChange(star)}
          className={`p-0.5 transition-colors ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
        >
          <Star
            size={24}
            className={`${
              star <= (hover || value)
                ? 'text-rune fill-rune'
                : 'text-stone/50'
            } transition-colors`}
          />
        </button>
      ))}
    </div>
  );
}

function DimensionSlider({
  value,
  onChange,
  label,
  description,
  disabled,
}: {
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  label: string;
  description: string;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <div>
          <span className="text-sm text-parchment">{label}</span>
          <span className="text-xs text-mist ml-2">{description}</span>
        </div>
        <span className="text-sm text-parchment font-mono">{value ?? '-'}</span>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={() => onChange(undefined)}
          disabled={disabled}
          className="text-xs text-mist hover:text-parchment px-1.5 py-0.5 rounded border border-stone/30 disabled:opacity-50"
        >
          Clear
        </button>
        <input
          type="range"
          min="1"
          max="5"
          value={value ?? 3}
          onChange={(e) => onChange(parseInt(e.target.value))}
          disabled={disabled}
          className="flex-1 h-2 bg-stone/30 rounded-lg appearance-none cursor-pointer accent-rune disabled:cursor-not-allowed"
        />
        <div className="flex gap-0.5">
          {[1, 2, 3, 4, 5].map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => onChange(v)}
              disabled={disabled}
              className={`w-6 h-6 rounded text-xs ${
                v === value
                  ? 'bg-rune text-night font-medium'
                  : 'bg-stone/20 text-mist hover:bg-stone/30'
              } disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {v}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export function RatingPanel({
  existingRating,
  isLoading,
  onSubmit,
  onDelete,
}: RatingPanelProps) {
  const [rating, setRating] = useState(existingRating?.rating ?? 0);
  const [notes, setNotes] = useState(existingRating?.notes ?? '');
  const [narrativeQuality, setNarrativeQuality] = useState<number | undefined>(
    existingRating?.narrativeQuality ?? undefined,
  );
  const [consistency, setConsistency] = useState<number | undefined>(
    existingRating?.consistency ?? undefined,
  );
  const [voiceAccuracy, setVoiceAccuracy] = useState<number | undefined>(
    existingRating?.voiceAccuracy ?? undefined,
  );
  const [engagement, setEngagement] = useState<number | undefined>(
    existingRating?.engagement ?? undefined,
  );
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Reset form when existing rating changes
  useEffect(() => {
    setRating(existingRating?.rating ?? 0);
    setNotes(existingRating?.notes ?? '');
    setNarrativeQuality(existingRating?.narrativeQuality ?? undefined);
    setConsistency(existingRating?.consistency ?? undefined);
    setVoiceAccuracy(existingRating?.voiceAccuracy ?? undefined);
    setEngagement(existingRating?.engagement ?? undefined);
  }, [existingRating]);

  const handleSubmit = async () => {
    if (rating === 0) return;
    setIsSaving(true);
    try {
      await onSubmit({
        rating,
        notes: notes || undefined,
        narrativeQuality,
        consistency,
        voiceAccuracy,
        engagement,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!existingRating) return;
    if (!window.confirm('Delete this rating?')) return;
    setIsDeleting(true);
    try {
      await onDelete();
    } finally {
      setIsDeleting(false);
    }
  };

  const isSubmitDisabled = rating === 0 || isSaving || isLoading;

  return (
    <div className="space-y-6">
      {/* Overall Rating */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-parchment">Overall Rating *</label>
        <div className="flex items-center gap-4">
          <StarRating value={rating} onChange={setRating} disabled={isLoading} />
          <span className="text-sm text-mist">
            {rating > 0 ? `${rating} / 5` : 'Select a rating'}
          </span>
        </div>
      </div>

      {/* Dimension Ratings */}
      <div className="space-y-4">
        <label className="block text-sm font-medium text-parchment">Dimension Ratings</label>
        <div className="space-y-4">
          <DimensionSlider
            label="Narrative Quality"
            description="Story coherence"
            value={narrativeQuality}
            onChange={setNarrativeQuality}
            disabled={isLoading}
          />
          <DimensionSlider
            label="Consistency"
            description="World alignment"
            value={consistency}
            onChange={setConsistency}
            disabled={isLoading}
          />
          <DimensionSlider
            label="Voice Accuracy"
            description="Character tone"
            value={voiceAccuracy}
            onChange={setVoiceAccuracy}
            disabled={isLoading}
          />
          <DimensionSlider
            label="Engagement"
            description="Reader interest"
            value={engagement}
            onChange={setEngagement}
            disabled={isLoading}
          />
        </div>
      </div>

      {/* Notes */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-parchment">Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={isLoading}
          placeholder="Add observations, issues, or feedback..."
          maxLength={2000}
          rows={4}
          className="w-full px-3 py-2 bg-void border border-stone/30 rounded-lg text-parchment text-sm focus:outline-none focus:border-rune resize-none disabled:opacity-50"
        />
        <div className="text-xs text-mist text-right">{notes.length}/2000</div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between gap-4 pt-4 border-t border-stone/20">
        <div className="flex items-center gap-2">
          {existingRating && (
            <button
              onClick={handleDelete}
              disabled={isDeleting || isLoading}
              className="flex items-center gap-1 px-3 py-2 text-sm text-ember hover:bg-ember/10 rounded-lg disabled:opacity-50"
            >
              {isDeleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
              Delete Rating
            </button>
          )}
        </div>
        <button
          onClick={handleSubmit}
          disabled={isSubmitDisabled}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-rune text-night hover:bg-amber-400 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving && <Loader2 size={14} className="animate-spin" />}
          {existingRating ? 'Update Rating' : 'Submit Rating'}
        </button>
      </div>

      {/* Last updated info */}
      {existingRating && (
        <div className="text-xs text-mist pt-2">
          Last rated by {existingRating.rater.email} on{' '}
          {new Date(existingRating.updatedAt).toLocaleString()}
        </div>
      )}
    </div>
  );
}
